/**
 * Why a message to the AI assistant got no answer (WI #5769, D10): the chat
 * shows it inline, in German, instead of the server's or the browser's own
 * (English) text. Pure, so the mapping is testable without a component.
 */
export type AiErrorKind =
  | 'network'
  | 'busy'
  | 'files'
  | 'model'
  | 'notFound'
  | 'rateLimit'
  | 'forbidden'
  | 'unknown';

/**
 * The kind of an error, from its text — the server's message (an SSE
 * `error` event, a stored answer's `error_message`, a response body) or the
 * browser's (a failed fetch) — and, for a refused request, its HTTP status.
 */
export function aiErrorKind(
  detail: string | null | undefined,
  status?: number,
): AiErrorKind {
  const text = detail ?? '';
  if (status === 401 || status === 403) {
    return 'forbidden';
  }
  if (status === 404 || /conversation not found/i.test(text)) {
    return 'notFound';
  }
  if (status === 429 || /rate limit|too many requests/i.test(text)) {
    return 'rateLimit';
  }
  // The workflows texts of `ai.write-access.ts` and of the model check.
  if (/previous answer is still running/i.test(text)) {
    return 'busy';
  }
  if (/pinned to its initial ai model/i.test(text)) {
    return 'model';
  }
  if (/files? .*no longer available/i.test(text)) {
    return 'files';
  }
  if (
    status === 502 ||
    status === 503 ||
    status === 504 ||
    /failed to fetch|network ?error|load failed|network request failed/i.test(
      text,
    )
  ) {
    return 'network';
  }

  return 'unknown';
}

/**
 * The same prompt may get its answer when sent again: the error may pass
 * (a dropped connection, a busy service, a chat that is gone — the store
 * starts a new one — or an unknown failure). Not a missing right, a file
 * that is gone or a chat pinned to another model: a resend meets them again.
 */
export function isRetryableAiError(kind: AiErrorKind): boolean {
  return (
    kind === 'network' ||
    kind === 'busy' ||
    kind === 'rateLimit' ||
    kind === 'notFound' ||
    kind === 'unknown'
  );
}
